import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import ApprovalHeader from "./components/approval-header";
import ApprovalTable from "./components/approval-table";
import ApprovalEmptyState from "./components/approval-empty-state";
import ApprovalLoading from "./components/approval-loading";
import ApprovalError from "./components/approval-error";
import ApprovalPagination from "./components/approval-pagination";
import VisualModal from "./components/visual-modal";
import MainLayout from "../../components/layout/MainLayout";
import {
  deleteApprovalFormService,
  getAdminProjectsWithApprovalForms,
} from "../../../Services/approvalFormService";
import { toast } from "react-toastify";
import { t } from "../../../utils/translate";

export default function ApprovalPage() {
  const adminId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visualModalOpen, setVisualModalOpen] = useState(false);
  const [selectedVisualData, setSelectedVisualData] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "projectName",
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const getApprovalsDataByUserId = async (id) => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const responseData = await getAdminProjectsWithApprovalForms(id);
      if (responseData) {
        const allApprovalForms = [];
        const approvalData = responseData?.data?.forEach((project) => {
          if (project.approvalForms && Array.isArray(project.approvalForms)) {
            allApprovalForms.push(...project.approvalForms);
          }
        });
        const filterAllApprovalForms = allApprovalForms?.filter((ele) => !ele.isDeleted);
        setApprovals(filterAllApprovalForms);
      } else {
        setApprovals([]);
      }
    } catch (err) {
      console.error("Error fetching approvals:", err);
      setError("Failed to load approvals. Please try again later.");
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminId) {
      getApprovalsDataByUserId(adminId);
    }
  }, [adminId]);

  // Filter approvals based on search term and status filter
  const filteredApprovals = useMemo(() => {
    return approvals?.filter((approval) => {
      const matchesSearch =
        approval.scaffoldId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.scaffoldUniqueNumber
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        approval.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.projectId?.projectName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        approval.projectId?.projectNumber
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        approval.scaffolderowner
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || approval.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [approvals, searchTerm, statusFilter]);

  const sortedApprovals = useMemo(() => {
    const sortableApprovals = [...filteredApprovals];

    // Map 'projectName' to nested 'projectId.projectName'
    const sortKey =
      sortConfig.key === "projectName"
        ? "projectId.projectName"
        : sortConfig.key;

    return sortableApprovals.sort((a, b) => {
      const getNestedProperty = (obj, path) => {
        const parts = path.split(".");
        return parts.reduce(
          (acc, part) => (acc && acc[part] !== undefined ? acc[part] : ""),
          obj
        );
      };

      let aValue = getNestedProperty(a, sortKey);
      let bValue = getNestedProperty(b, sortKey);

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredApprovals, sortConfig]);

  const paginatedApprovals = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedApprovals.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedApprovals, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedApprovals.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }

    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Jump to specific page
  const handleJumpToPage = (page) => {
    const pageNumber = Number.parseInt(page, 10);
    if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle opening visual modal
  const handleOpenVisualModal = (visualData) => {
    setSelectedVisualData(visualData);
    setVisualModalOpen(true);
  };

  // Handle closing visual modal
  const handleCloseVisualModal = () => {
    setVisualModalOpen(false);
    setSelectedVisualData(null);
  };

  const handleDelete = async (id) => {
    const response = await deleteApprovalFormService(id);
    if (response?.data?.status === "success") {
      toast.success(t("deletedSuccessfully"));
      getApprovalsDataByUserId(adminId);
    } else {
      toast.error(t("thereIsSomeError"));
    }
  };

  return (
    <MainLayout title="Approvals">
      <div className="space-y-6">
        <ApprovalHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        {loading ? (
          <ApprovalLoading />
        ) : error ? (
          <ApprovalError error={error} />
        ) : approvals.length === 0 ? (
          <ApprovalEmptyState />
        ) : (
          <div className="space-y-4">
            <ApprovalTable
              approvals={paginatedApprovals}
              requestSort={requestSort}
              sortConfig={sortConfig}
              onViewVisual={handleOpenVisualModal}
              handleDelete={handleDelete}
            />

            <ApprovalPagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              handlePageChange={handlePageChange}
              handleJumpToPage={handleJumpToPage}
              totalItems={sortedApprovals.length}
            />
          </div>
        )}
      </div>

      {visualModalOpen && (
        <VisualModal
          isOpen={visualModalOpen}
          onClose={handleCloseVisualModal}
          visualData={selectedVisualData}
        />
      )}
    </MainLayout>
  );
}
